// @ts-nocheck - Until react-spring fixes their typings (currently in v9 beta)

import { Box, BoxProps, chakra, ColorProps, Flex } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { animated, useTransition } from 'react-spring'

import { IconByName, IconNames } from '~shared-components/icons/brand'
import { IconClose } from '~shared-components/icons/brand/IconClose'

import { generateUniqueId } from '../../utils'
import { StandardText } from '..'
import { ToasterSelector } from './Redux/selectors'

export interface ToastType {
  canClose?: boolean
  closeOnClick?: boolean
  color?: ColorProps['color']
  icon?: IconNames
  key?: string
  message: string | React.ReactElement
  timeout?: number
}

type HorizontalAlignment = 'left' | 'right' | 'center'
type VerticalAlignment = 'top' | 'bottom'

const Overlay: React.FC<BoxProps> = props => (
  <Box
    bottom={0}
    left={0}
    pointerEvents="none"
    position="fixed"
    px={[5, 10, 20]}
    right={0}
    top={0}
    zIndex={3000}
    {...props}
  />
)

function getHorizontalAlignment(alignHorizontal) {
  if (alignHorizontal === 'left') return '0 auto 0 0'
  if (alignHorizontal === 'right') return '0 0 0 auto'
  if (alignHorizontal === 'center') return '0 auto'
}

interface ToastContainerProps extends BoxProps {
  alignHorizontal: HorizontalAlignment
  alignVertical: VerticalAlignment
}
const ToastContainer: React.FC<ToastContainerProps> = ({
  alignHorizontal = 'center',
  alignVertical = 'bottom',
  ...props
}) => {
  const alignProps = {
    [alignVertical]: '20px',
    margin: getHorizontalAlignment(alignHorizontal),
  }
  return (
    <Box
      left="20px"
      maxWidth="450px"
      paddingBottom="20px"
      position="absolute"
      right="20px"
      {...alignProps}
      {...props}
    />
  )
}

interface ToastProps extends BoxProps {
  bgColor: ColorProps['color']
}
const sxToast = {
  '.typography-text': {
    '& > a': {
      color: 'white.0',
      textDecoration: 'underline',
    },
    color: 'white.0',
    flexGrow: 1,
  },
  svg: {
    color: 'white.0',
  },
}
const AnimatedDivInherited = chakra(animated.div)
const Toast: React<ToastProps> = ({ bgColor, ...props }) => (
  <AnimatedDivInherited
    background={bgColor}
    borderRadius="3px"
    boxShadow="0 2px 6px 0 rgba(0, 0, 0, 0.1)"
    color="white.0"
    pointerEvents="all"
    position="relative"
    sx={sxToast}
    userSelect="none"
    w="100%"
    whiteSpace="pre-wrap"
    zIndex={1}
    {...props}
  />
)

const sxToastBody = {
  '& > *': {
    padding: 4,
  },
}

interface ToastBodyProps extends FlexProps {
  fullWidth?: boolean
}
const ToastBody: React.FC<ToastBodyProps> = React.forwardRef(
  ({ fullWidth, ...props }, ref) => (
    <Flex
      ref={ref}
      sx={sxToastBody}
      w={fullWidth ? '100%' : 'unset'}
      {...props}
    />
  ),
)
ToastBody.displayName = 'ToastBoady'

interface ToasterProps {
  alignHorizontal?: HorizontalAlignment
  alignVertical?: VerticalAlignment
  config?: {}
  onFinishedToasting?: (toast: ToastType) => void
}

export const Toaster = ({
  alignHorizontal,
  alignVertical,
  config = { friction: 20, precision: 0.1, tension: 200 },
  onFinishedToasting,
}: ToasterProps) => {
  const toasts = useSelector(ToasterSelector.toasts)
  const [refMap] = useState(() => new WeakMap())
  const [cancelMap] = useState(() => new WeakMap())
  const [items, setItems] = useState<ToastType[]>([])

  const transitions = useTransition(items, item => item.key, {
    config,
    enter: item => async next => {
      await next({
        height: refMap.get(item).offsetHeight,
        marginTop: 15,
        opacity: 1,
      })
    },
    from: { height: 0, life: '100%', marginTop: 0, opacity: 0 },
    leave: item => async (next, cancel) => {
      cancelMap.set(item, cancel)
      await next({
        config: {
          duration: item.timeout === 0 ? Number.MAX_SAFE_INTEGER : item.timeout,
        },
        life: '0%',
      })
      await next({ opacity: 0 })
      await next({ height: 0, marginTop: 0 })
    },
    onRest: item => setItems(state => state.filter(i => i.key !== item.key)),
  })

  useEffect(() => {
    setItems(items => {
      const newToasts = toasts
        .filter(toast => !items.some(i => i.key === toast.key))
        .map(immutableItem => {
          const item = { ...immutableItem } // this is an Immer object from Redux
          const { highlight, icon, key, timeout } = item

          if (!icon) {
            switch (highlight) {
              case 'success':
                item.icon = 'checkmark'
                break
              case 'error':
                item.icon = 'block'
            }
          }

          if (timeout === undefined) {
            item.timeout =
              typeof item.message === 'string'
                ? item.message.length * 25 + 2000
                : 2500
          }

          if (!key) item.key = generateUniqueId()

          if (onFinishedToasting) onFinishedToasting(item)
          return item
        })

      return [...items, ...newToasts]
    })
  }, [onFinishedToasting, toasts])

  return (
    <Overlay>
      <ToastContainer
        alignHorizontal={alignHorizontal}
        alignVertical={alignVertical}
      >
        {transitions.map(({ item, key, props: { canClose, ...style } }) => {
          return (
            <Toast key={key} bgColor={item.color} style={style}>
              <ToastBody
                ref={ref => ref && refMap.set(item, ref)}
                fullWidth
                justifyContent="space-between"
                onClick={
                  item.closeOnClick
                    ? () => cancelMap.has(item) && cancelMap.get(item)()
                    : undefined
                }
              >
                <Box>
                  {item.icon && (
                    <Flex
                      alignItems="center"
                      background="rgba(0, 0, 0, 0.1)"
                      justifyContent="center"
                    >
                      <IconByName boxSize="24px" name={item.icon} />
                    </Flex>
                  )}
                  <StandardText color="white.0">{item.message}</StandardText>
                </Box>
                {!canClose && (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    onClick={e => {
                      e.stopPropagation()
                      cancelMap.has(item) && cancelMap.get(item)()
                    }}
                  >
                    <IconClose boxSize="20px" />
                  </Flex>
                )}
              </ToastBody>
            </Toast>
          )
        })}
      </ToastContainer>
    </Overlay>
  )
}
